/**
 * Plan-tier feature gates for partner UI. Mirrors entitlements from
 * GET /partners/plan — keep in sync with backend/config/plans.js.
 */

export const isBespokePartnerPlan = (entitlements) => (
  String(entitlements?.tier || '').toLowerCase() === 'bespoke'
);

export const canCreatePartnerGroupTrips = (entitlements) => {
  if (!entitlements) return null;
  const limit = entitlements.limits?.maxGroupTrips;
  if (limit === -1 || limit == null) return true;
  return limit > 0;
};

export const canUseMarketplaceListing = (entitlements) => (
  Boolean(entitlements?.features?.hasMarketplaceListing)
);

export const canUseFeaturedPlacement = (entitlements) => (
  Boolean(entitlements?.features?.hasFeaturedPlacement)
);

export const canUseGooglePlacesBrowse = (entitlements) => (
  Boolean(entitlements?.features?.hasGooglePlacesBrowse)
);

export const canUsePartnerAiPlanning = (entitlements) => (
  Boolean(entitlements?.features?.hasAiPlanning)
);

export const canUsePartnerClientInvite = (entitlements) => {
  if (!entitlements) return false;
  if (entitlements.features && Object.prototype.hasOwnProperty.call(entitlements.features, 'hasClientInvite')) {
    return Boolean(entitlements.features.hasClientInvite);
  }
  return String(entitlements.tier || '').toLowerCase() === 'bespoke';
};

export const canUsePartnerWhiteLabel = (entitlements) => {
  if (!entitlements) return false;
  if (entitlements.features && Object.prototype.hasOwnProperty.call(entitlements.features, 'hasWhiteLabel')) {
    return Boolean(entitlements.features.hasWhiteLabel);
  }
  return String(entitlements.tier || '').toLowerCase() === 'bespoke';
};

export const canUseBrandedItineraries = (entitlements) => (
  Boolean(entitlements?.features?.hasBrandedItineraries)
);

export const canUseTeamReporting = (entitlements) => (
  Boolean(entitlements?.features?.hasTeamReporting)
);

/** Agency+ sandbox or production Developer API add-on / Enterprise. */
export const canUseDeveloperApiSandbox = (entitlements) => (
  Boolean(entitlements?.features?.hasApiSandbox)
);

export const canUseDeveloperProductionApi = (entitlements) => (
  Boolean(entitlements?.features?.hasProductionApi)
);

/**
 * Sandbox exploration, webhooks, and trip sync tools.
 * Package API keys stay available without this (see DeveloperLayout keys nav).
 */
export const canAccessDeveloperApiTools = (entitlements) => (
  canUseDeveloperApiSandbox(entitlements) || canUseDeveloperProductionApi(entitlements)
);

/** Paths that need sandbox or paid trip API (keys are free for package upload). */
export const DEVELOPER_API_TOOL_PATHS = new Set(['webhooks', 'usage']);

export const isDeveloperApiToolPath = (path = '') => (
  DEVELOPER_API_TOOL_PATHS.has(String(path).replace(/^\/+/, '').split('/')[0])
);

/** Evaluate every gate the partner UI cares about. */
export const evaluatePartnerTierGates = (entitlements) => ({
  canCreateGroupTrips: canCreatePartnerGroupTrips(entitlements) === true,
  canUseMarketplaceListing: canUseMarketplaceListing(entitlements),
  canUseFeaturedPlacement: canUseFeaturedPlacement(entitlements),
  canUseGooglePlacesBrowse: canUseGooglePlacesBrowse(entitlements),
  canUseAiPlanning: canUsePartnerAiPlanning(entitlements),
  canUseClientInvite: canUsePartnerClientInvite(entitlements),
  canUseWhiteLabel: canUsePartnerWhiteLabel(entitlements),
  canUseBrandedItineraries: canUseBrandedItineraries(entitlements),
  canUseTeamReporting: canUseTeamReporting(entitlements),
});

/** Expected gates per tier — single source for unit tests and docs. */
export const TIER_GATE_EXPECTATIONS = {
  free: {
    canCreateGroupTrips: false,
    canUseMarketplaceListing: true,
    canUseFeaturedPlacement: false,
    canUseGooglePlacesBrowse: false,
    canUseAiPlanning: false,
    canUseClientInvite: false,
    canUseWhiteLabel: false,
    canUseBrandedItineraries: false,
    canUseTeamReporting: false,
  },
  pro: {
    canCreateGroupTrips: true,
    canUseMarketplaceListing: true,
    canUseFeaturedPlacement: false,
    canUseGooglePlacesBrowse: false,
    canUseAiPlanning: false,
    canUseClientInvite: false,
    canUseWhiteLabel: false,
    canUseBrandedItineraries: false,
    canUseTeamReporting: false,
  },
  group: {
    canCreateGroupTrips: true,
    canUseMarketplaceListing: true,
    canUseFeaturedPlacement: false,
    canUseGooglePlacesBrowse: false,
    canUseAiPlanning: true,
    canUseClientInvite: false,
    canUseWhiteLabel: false,
    canUseBrandedItineraries: false,
    canUseTeamReporting: false,
  },
  agency: {
    canCreateGroupTrips: true,
    canUseMarketplaceListing: true,
    canUseFeaturedPlacement: true,
    canUseGooglePlacesBrowse: true,
    canUseAiPlanning: true,
    canUseClientInvite: false,
    canUseWhiteLabel: false,
    canUseBrandedItineraries: false,
    canUseTeamReporting: true,
  },
  bespoke: {
    canCreateGroupTrips: true,
    canUseMarketplaceListing: false,
    canUseFeaturedPlacement: false,
    canUseGooglePlacesBrowse: true,
    canUseAiPlanning: true,
    canUseClientInvite: true,
    canUseWhiteLabel: true,
    canUseBrandedItineraries: true,
    canUseTeamReporting: true,
  },
};

export const PARTNER_TIER_IDS = Object.keys(TIER_GATE_EXPECTATIONS);

/** Build entitlements-shaped object from API limits + features (for tests). */
export const entitlementsFixture = (tier, overrides = {}) => {
  const base = TIER_GATE_EXPECTATIONS[tier] || TIER_GATE_EXPECTATIONS.free;
  return {
    tier,
    planName: tier,
    limits: {
      maxGroupTrips: base.canCreateGroupTrips ? -1 : 0,
      maxClientTrips: tier === 'free' ? 3 : -1,
      maxTeamMembers: tier === 'free' ? 1 : tier === 'pro' ? 1 : tier === 'group' ? 3 : tier === 'agency' ? 5 : -1,
      ...overrides.limits,
    },
    features: {
      hasMarketplaceListing: base.canUseMarketplaceListing,
      hasFeaturedPlacement: base.canUseFeaturedPlacement,
      hasGooglePlacesBrowse: base.canUseGooglePlacesBrowse,
      hasAiPlanning: base.canUseAiPlanning,
      hasClientInvite: base.canUseClientInvite,
      hasWhiteLabel: base.canUseWhiteLabel,
      hasBrandedItineraries: base.canUseBrandedItineraries,
      hasTeamReporting: base.canUseTeamReporting,
      hasApiSandbox: tier === 'agency' || tier === 'bespoke',
      hasProductionApi: tier === 'bespoke',
      ...overrides.features,
    },
    ...overrides,
  };
};
